import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Grid, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteConfirmationDialog from '../../components/common/DeleteConfirmationDialog';
import { toast } from '../../components/toast';
import { getUserGroups } from '../../utils';
import { useEmployeeOperations } from '../../hooks/useEmployeeOperations';
import { employeeValidationSchema, userCreationValidationSchema } from '../../utils/employeeValidation';
import { generateUsernameAndNames, getInitialFormData, getInitialUserFormData } from '../../utils/employeeUtils';
import { downloadEmployeesXLSX, getEmployeeShifts } from '../../services/api';
import {
  EmployeeStatsCards,
  EmployeeTable,
  EmployeeForm,
  UserCreationForm,
  EmployeeFilters,
} from '../../components/employee';
import { HRPageHeader } from '../../components/hr';
import { LightTooltip } from '../../components/custom-components/custom-tooltip';

const allowedGroups = ['admin', 'hr'];

export default function EmployeeManagementPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [designationFilter, setDesignationFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
    preset: 'All',
  });

  const {
    isLoading,
    employees,
    stats,
    totalCount,
    filterOptions,
    loadEmployees,
    createEmployeeRecord,
    updateEmployeeRecord,
    deleteEmployeeRecord,
    createUserAccount,
  } = useEmployeeOperations();

  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [userFormData, setUserFormData] = useState(getInitialUserFormData());
  const [userFormErrors, setUserFormErrors] = useState({});

  const [formData, setFormData] = useState(getInitialFormData());
  const [formErrors, setFormErrors] = useState({});
  const [employeeShifts, setEmployeeShifts] = useState([]);

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !allowedGroups.some((group) => userGroups.includes(group))) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    loadEmployees(
      page,
      rowsPerPage,
      searchTerm,
      departmentFilter,
      designationFilter,
      statusFilter,
      dateRange.startDate,
      dateRange.endDate,
      sortBy,
      sortDirection
    );
  }, [
    page,
    rowsPerPage,
    searchTerm,
    departmentFilter,
    designationFilter,
    statusFilter,
    dateRange.startDate,
    dateRange.endDate,
    sortBy,
    sortDirection,
    loadEmployees,
  ]);

  useEffect(() => {
    const loadEmployeeShifts = async () => {
      try {
        const response = await getEmployeeShifts(1, 1000);
        setEmployeeShifts(response?.data?.results || []);
      } catch (error) {
        console.error('Error loading employee shifts:', error);
      }
    };

    loadEmployeeShifts();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (mode, employee = null) => {
    setDialogMode(mode);
    setEditingEmployee(employee);
    if ((mode === 'edit' || mode === 'view') && employee) {
      setFormData({
        employee_id: employee.employee_id || '',
        full_name: employee.full_name || '',
        email: employee.email || '',
        phone: employee.phone || '',
        identity_number: employee.identity_number || '',
        shift: employee.shift?.id || '',
        designation: employee.designation || '',
        department: employee.department || '',
        basic_salary: employee.basic_salary ? employee.basic_salary.toString() : '',
        salary_currency: employee.salary_currency || 'PKR',
        transport_allowance: employee.transport_allowance ? employee.transport_allowance.toString() : '0',
        other_allowances: employee.other_allowances ? employee.other_allowances.toString() : '0',
        tax_deductions: employee.tax_deductions ? employee.tax_deductions.toString() : '0',
        take_home_salary: employee.take_home_salary ? employee.take_home_salary.toString() : '',
        joining_date: employee.joining_date || '',
        bank_name: employee.bank_name || '',
        bank_account_number: employee.bank_account_number || '',
        bank_account_title: employee.bank_account_title || '',
        resignation_date: employee.resignation_date || null,
        is_active: employee.is_active !== undefined ? employee.is_active : true,
        remarks: employee.remarks || '',
      });
    } else {
      setFormData(getInitialFormData());
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEmployee(null);
    setFormErrors({});
    setFormData(getInitialFormData());
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = async () => {
    try {
      await employeeValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    const employeeData = {
      employee_id: formData.employee_id,
      full_name: formData.full_name,
      email: formData.email.trim() !== '' ? formData.email : null,
      phone: formData.phone,
      identity_number: formData.identity_number || null,
      shift: formData.shift || null,
      designation: formData.designation,
      department: formData.department,
      basic_salary: formData.basic_salary ? parseFloat(formData.basic_salary) : null,
      salary_currency: formData.salary_currency,
      transport_allowance: formData.transport_allowance ? parseFloat(formData.transport_allowance) : null,
      other_allowances: formData.other_allowances ? parseFloat(formData.other_allowances) : null,
      tax_deductions: formData.tax_deductions ? parseFloat(formData.tax_deductions) : null,
      take_home_salary: formData.take_home_salary ? parseFloat(formData.take_home_salary) : null,
      joining_date: formData.joining_date || null,
      resignation_date: formData.resignation_date || null,
      bank_name: formData.bank_name || null,
      bank_account_number: formData.bank_account_number || null,
      bank_account_title: formData.bank_account_title || null,
      remarks: formData.remarks,
      is_active: formData.is_active !== undefined ? formData.is_active : true,
    };

    let success = false;
    if (dialogMode === 'edit' && editingEmployee) {
      success = await updateEmployeeRecord(editingEmployee.id, employeeData);
    } else {
      success = await createEmployeeRecord(employeeData);
    }

    if (success) {
      handleCloseDialog();
      loadEmployees(
        page,
        rowsPerPage,
        searchTerm,
        departmentFilter,
        designationFilter,
        statusFilter,
        dateRange.startDate,
        dateRange.endDate,
        sortBy,
        sortDirection
      );
    }
  };

  const handleDeleteEmployee = (employee) => {
    setEmployeeToDelete(employee);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (employeeToDelete) {
      const success = await deleteEmployeeRecord(employeeToDelete.id);
      if (success) {
        setOpenDeleteDialog(false);
        setEmployeeToDelete(null);
        loadEmployees(
          page,
          rowsPerPage,
          searchTerm,
          departmentFilter,
          designationFilter,
          statusFilter,
          dateRange.startDate,
          dateRange.endDate,
          sortBy,
          sortDirection
        );
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setEmployeeToDelete(null);
  };

  const handleOpenUserDialog = (employee) => {
    setSelectedEmployee(employee);
    const generatedData = generateUsernameAndNames(employee.full_name);
    setUserFormData({
      ...generatedData,
      password: '',
    });
    setUserFormErrors({});
    setOpenUserDialog(true);
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    setSelectedEmployee(null);
    setUserFormData(getInitialUserFormData());
    setUserFormErrors({});
  };

  const handleUserInputChange = (field, value) => {
    setUserFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (userFormErrors[field]) {
      setUserFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateUserForm = async () => {
    try {
      await userCreationValidationSchema.validate(userFormData, { abortEarly: false });
      setUserFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setUserFormErrors(errors);
      return false;
    }
  };

  const handleCreateUserAccount = async () => {
    const isValid = await validateUserForm();
    if (!isValid) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    const userData = {
      employee_id: selectedEmployee.id,
      username: userFormData.username,
      first_name: userFormData.first_name,
      last_name: userFormData.last_name,
      password: userFormData.password,
    };

    const success = await createUserAccount(userData);
    if (success) {
      handleCloseUserDialog();
      loadEmployees(
        page,
        rowsPerPage,
        searchTerm,
        departmentFilter,
        designationFilter,
        statusFilter,
        dateRange.startDate,
        dateRange.endDate,
        sortBy,
        sortDirection
      );
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'department':
        setDepartmentFilter(value);
        break;
      case 'designation':
        setDesignationFilter(value);
        break;
      case 'status':
        setStatusFilter(value);
        break;
      default:
        break;
    }
    setPage(0);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    setPage(0);
  };

  const handleSortChange = (newSortBy, newSortDirection) => {
    setSortBy(newSortBy || '');
    setSortDirection(newSortDirection || 'asc');
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('All');
    setDesignationFilter('All');
    setStatusFilter('All');
    setSortBy('');
    setSortDirection('asc');
    setDateRange({
      startDate: null,
      endDate: null,
      preset: 'All',
    });
    setPage(0);
  };

  const handleDownloadXLSX = async () => {
    try {
      // Prepare filters for the download
      const filters = {
        search: searchTerm,
        department: departmentFilter,
        designation: designationFilter,
        status: statusFilter,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        sort_by: sortBy,
        sort_direction: sortDirection,
      };

      // Call the API
      const response = await downloadEmployeesXLSX(filters);

      // Create blob URL and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'employees_export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Employees data downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download employees data');
    }
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Employee Management</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HRPageHeader
            title="Employee management"
            description="Maintain employee profiles, portal accounts, and directory data. Filter by department, status, or joining date, then export or add new staff."
            actions={
              <>
                <LightTooltip title="Export the current filtered employee list to Excel.">
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadXLSX}
                      disabled={isLoading}
                    >
                      Download Excel
                    </Button>
                  </span>
                </LightTooltip>
                <LightTooltip title="Create a new employee record and optional portal account.">
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('add')}>
                    Add employee
                  </Button>
                </LightTooltip>
              </>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <EmployeeStatsCards stats={stats} isLoading={isLoading} />
        </Grid>

        <Grid item xs={12}>
          <EmployeeFilters
            searchTerm={searchTerm}
            departmentFilter={departmentFilter}
            statusFilter={statusFilter}
            filterOptions={filterOptions}
            dateRange={dateRange}
            onSearchChange={handleSearchChange}
            onFilterChange={handleFilterChange}
            onDateRangeChange={handleDateRangeChange}
            onClearFilters={handleClearFilters}
          />
        </Grid>

        <Grid item xs={12}>
          <EmployeeTable
            employees={employees}
            isLoading={isLoading}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onViewEmployee={(employee) => handleOpenDialog('view', employee)}
            onEditEmployee={(employee) => handleOpenDialog('edit', employee)}
            onDeleteEmployee={handleDeleteEmployee}
            onCreateUserAccount={handleOpenUserDialog}
          />
        </Grid>
      </Grid>

      <EmployeeForm
        open={openDialog}
        mode={dialogMode}
        formData={formData}
        formErrors={formErrors}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
        shifts={employeeShifts}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        title="Confirm Delete"
        message="Are you sure you want to delete the employee"
        itemToDelete={employeeToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmButtonText="Delete Employee"
      />

      <UserCreationForm
        open={openUserDialog}
        selectedEmployee={selectedEmployee}
        formData={userFormData}
        formErrors={userFormErrors}
        onClose={handleCloseUserDialog}
        onSubmit={handleCreateUserAccount}
        onInputChange={handleUserInputChange}
      />
    </>
  );
}
